import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, Typography, Box, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, Grid, IconButton, Avatar, Badge, CircularProgress,
  Paper, Divider, Button, Tooltip, Snackbar, Alert, TableContainer
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RouterIcon from "@mui/icons-material/Router";
import SimCardIcon from "@mui/icons-material/SimCard";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import PlaceIcon from "@mui/icons-material/Place";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

import {
  getModemById,
  getHistorialByNumSim,
  searchFlespiPrimary,
  searchFlespiSecondary,
  searchWialonUnits,
  searchLeasecontrolUnits,
} from "../services/modems";
import { fmtFechaHoraSql } from "../utils/fechas";

// --- HELPERS UI CON COLOR ---
const DetailRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
    <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
    <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ flex: 1 }}>{value || "—"}</Typography>
  </Box>
);

const TelemetryRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="caption" fontWeight={600} color="text.primary">{value || "—"}</Typography>
  </Box>
);

const StatusChip = ({ status }) => {
  const isOk = status === "Activo" || status === true || status === "A";
  const isPending = status === "Pendiente";
  
  const bgcolor = isOk ? "#e6f4ea" : isPending ? "#fef7e0" : "#fce8e6";
  const color = isOk ? "#137333" : isPending ? "#b06000" : "#c5221f";

  return (
    <Chip 
      label={status === true ? "ON" : status === false ? "OFF" : status} 
      size="small"
      sx={{ bgcolor, color, fontWeight: 700, height: 22, fontSize: "0.65rem", borderRadius: 1.5 }} 
    />
  );
};

const formatGrupos = (grupos) => {
  if (!Array.isArray(grupos) || !grupos.length) return "—";
  return grupos.map((g) => g.nm || g.id).join(", ");
};

const formatUsuarios = (usuarios) => {
  if (!Array.isArray(usuarios) || !usuarios.length) return "—";
  return usuarios.map((u) => u.nm || u.id).join(", ");
};

export default function ModemViewerDialog({ open, onClose, modemId }) {
  const [loading, setLoading] = useState(false);
  const [modem, setModem] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [error, setError] = useState("");
  const [platforms, setPlatforms] = useState({ flespi1: null, flespi2: null, wialon: null, leasecontrol: null });
  const [platformsLoading, setPlatformsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const formatUnixDateTime = (ts) => {
    if (!ts && ts !== 0) return "—";
    const num = Number(ts);
    if (Number.isNaN(num)) return "—";
    const ms = num < 1e12 ? num * 1000 : num;
    const dtf = new Intl.DateTimeFormat("es-BO", {
      timeZone: "America/La_Paz", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    return dtf.format(new Date(ms)).replace(",", "");
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
  };

  useEffect(() => {
    if (!open || !modemId) return;
    const fetchData = async () => {
      setLoading(true); setError(""); setPlatformsLoading(false);
      try {
        const resp = await getModemById(modemId);
        const data = resp.data;
        setModem(data);

        if (data?.num_sim) {
          const hResp = await getHistorialByNumSim(data.num_sim);
          setHistorial(hResp.data || []);
        }

        const searchValues = [data.num_sim, data.emai].filter(Boolean);
        if (searchValues.length > 0) {
          setPlatformsLoading(true);
          const [f1, f2, wi, le] = await Promise.allSettled([
            searchFlespiPrimary(searchValues), searchFlespiSecondary(searchValues),
            searchWialonUnits(searchValues), searchLeasecontrolUnits(searchValues),
          ]);
          setPlatforms({
            flespi1: f1.status === "fulfilled" ? f1.value.data?.[0] : null,
            flespi2: f2.status === "fulfilled" ? f2.value.data?.[0] : null,
            wialon: wi.status === "fulfilled" ? wi.value.data?.data?.[0] : null,
            leasecontrol: le.status === "fulfilled" ? le.value.data?.data?.[0] : null,
          });
          setPlatformsLoading(false);
        }
      } catch (err) { setError("Error al cargar la información del módem."); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [open, modemId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, bgcolor: "#ffffff" } }}>
      
      {/* TÍTULO SUPERIOR (Con sutil fondo de color) */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2, bgcolor: "#f8f9fa" }}>
        <Typography variant="subtitle2" fontWeight={800} color="primary.800" sx={{ letterSpacing: 0.5 }}>
          DETALLES DEL MÓDEM
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "grey.600" }}><CloseIcon /></IconButton>
      </Box>
      <Divider sx={{ borderColor: "grey.200" }} />

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 10, textAlign: "center", color: "primary.main" }}>
            <CircularProgress color="inherit" />
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>Sincronizando plataformas...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
        ) : modem && (
          <Box sx={{ p: 3 }}>
            
            {/* 1. SECCIÓN PRINCIPAL: PERFIL Y DETALLES */}
            <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 3, p: 3, mb: 3, bgcolor: "#ffffff", boxShadow: "0px 4px 20px rgba(0,0,0,0.02)" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} sx={{ borderRight: { md: "1px dashed" }, borderColor: { md: "grey.200" } }}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
                    <Badge overlap="circular" anchorOrigin={{ vertical: "bottom", horizontal: "right" }} variant="dot" color={modem.estado === "Activo" ? "success" : "error"} sx={{ "& .MuiBadge-badge": { width: 14, height: 14, borderRadius: "50%", border: "2px solid white" } }}>
                      {/* Avatar con Gradiente */}
                      <Avatar sx={{ width: 70, height: 70, background: "linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)", color: "#ffffff", boxShadow: "0 4px 10px rgba(25, 118, 210, 0.3)" }}>
                        <RouterIcon fontSize="large" />
                      </Avatar>
                    </Badge>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="primary.main" fontWeight={700}>ID: {modem.id}</Typography>
                        <Tooltip title="Copiar ID" placement="top">
                          <ContentCopyIcon onClick={() => handleCopy(modem.id)} sx={{ fontSize: 14, color: "primary.main", cursor: "pointer", opacity: 0.7, "&:hover": { opacity: 1 } }} />
                        </Tooltip>
                      </Box>
                      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, mb: 1, color: "grey.900" }}>{modem.emai}</Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">{modem.marca} {modem.modelo}</Typography>
                        <StatusChip status={modem.estado} />
                      </Box>
                    </Box>
                  </Box>
                  {modem.observacion && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", bgcolor: "#fff8e1", border: "1px solid #ffecb3", p: 1.5, borderRadius: 2 }}>
                      <strong style={{ color: "#f57c00" }}>Observación:</strong> {modem.observacion}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DetailRow icon={<SimCardIcon fontSize="small" sx={{ color: "#d81b60" }} />} label="SIM Actual" value={modem.num_sim} />
                      <DetailRow icon={<DashboardIcon fontSize="small" sx={{ color: "#1976d2" }} />} label="Placa" value={modem.placa} />
                      <DetailRow icon={<PlaceIcon fontSize="small" sx={{ color: "#00897b" }} />} label="Ciudad" value={modem.ciudad} />
                      <DetailRow icon={<InfoOutlinedIcon fontSize="small" sx={{ color: "grey.500" }} />} label="Bandera" value={modem.bandera || 'No'} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow icon={<AssignmentIndIcon fontSize="small" sx={{ color: "#f57c00" }} />} label="Asignado" value={`${modem.nombre_normal || ''} ${modem.descripcion_normal || ''}`} />
                      <DetailRow icon={<AssignmentIndIcon fontSize="small" sx={{ color: "#f57c00" }} />} label="YPFB" value={`${modem.nombre_ypfb || ''} ${modem.descripcion_ypfb || ''}`} />
                      <DetailRow icon={<InfoOutlinedIcon fontSize="small" sx={{ color: "grey.500" }} />} label="Propiedad" value={modem.propiedad === "TRS" ? "TRS" : modem.nombre_normal} />
                      <DetailRow icon={<InfoOutlinedIcon fontSize="small" sx={{ color: "grey.500" }} />} label="Registro" value={fmtFechaHoraSql(modem.fecha_registro)} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {/* 2. SECCIÓN CENTRAL: TELEMETRÍA (Con identidades de color) */}
            <Typography variant="subtitle1" fontWeight={700} color="grey.800" mb={1.5} display="flex" alignItems="center">
              Telemetría en Tiempo Real {platformsLoading && <CircularProgress size={16} sx={{ ml: 1.5 }} thickness={5} />}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", border: "1px solid", borderColor: "grey.200", borderRadius: 2, mb: 3, overflow: "hidden", bgcolor: "#fafafa" }}>
              
              {[
                { id: "Flespi 1", d: platforms.flespi1, keyName: "name", color: "#1976d2", bg: "#f0f7ff",
                  rows: [
                    { l: "Ident", v: platforms.flespi1?.configuration?.ident },
                    { l: "Ignición", v: platforms.flespi1 ? <StatusChip status={platforms.flespi1.telemetry?.["engine.ignition.status"]} /> : null },
                    { l: "Velocidad", v: platforms.flespi1 ? `${platforms.flespi1.telemetry?.position?.speed ?? platforms.flespi1.telemetry?.["position.speed"] ?? 0} km/h` : null },
                    { l: "Último", v: formatUnixDateTime(platforms.flespi1?.telemetry?.timestamp) }
                  ]
                },
                { id: "Flespi 2", d: platforms.flespi2, keyName: "name", color: "#0288d1", bg: "#f0f9ff",
                  rows: [
                    { l: "Ident", v: platforms.flespi2?.configuration?.ident },
                    { l: "Ignición", v: platforms.flespi2 ? <StatusChip status={platforms.flespi2.telemetry?.["engine.ignition.status"]} /> : null },
                    { l: "Batería", v: platforms.flespi2 ? `${platforms.flespi2.telemetry?.["battery.level"] ?? '—'}%` : null },
                    { l: "Último", v: formatUnixDateTime(platforms.flespi2?.telemetry?.["server.timestamp"]) }
                  ]
                },
                { id: "Wialon", d: platforms.wialon, keyName: "nm", color: "#ed6c02", bg: "#fff7f0",
                  rows: [
                    { l: "UID", v: platforms.wialon?.uid },
                    { l: "GSM", v: platforms.wialon?.lmsg?.p?.gsm ?? '—' },
                    { l: "Voltaje", v: platforms.wialon ? `${platforms.wialon.lmsg?.p?.voltage ?? '—'} V` : null },
                    { l: "Último", v: formatUnixDateTime(platforms.wialon?.lmsg?.t) },
                    { l: "Grupo", v: formatGrupos(platforms.wialon?.grupos) },
                    { l: "Usuario", v: formatUsuarios(platforms.wialon?.usuarios) }
                  ]
                },
                { id: "LeaseControl", d: platforms.leasecontrol, keyName: "nm", color: "#9c27b0", bg: "#fbf0ff", noBorder: true,
                  rows: [
                    { l: "UID", v: platforms.leasecontrol?.uid },
                    { l: "Comando", v: platforms.leasecontrol?.lmsg?.p?.command || '—' },
                    { l: "GPS Válido", v: platforms.leasecontrol ? <StatusChip status={platforms.leasecontrol.lmsg?.p?.gps_valid} /> : null },
                    { l: "Último", v: formatUnixDateTime(platforms.leasecontrol?.lmsg?.t) },
                    { l: "Grupo", v: formatGrupos(platforms.leasecontrol?.grupos) },
                    { l: "Usuario", v: formatUsuarios(platforms.leasecontrol?.usuarios) }
                  ]
                }
              ].map((plt) => (
                <Box key={plt.id} sx={{ flex: 1, minWidth: 200, p: 2, borderRight: plt.noBorder ? "none" : "1px solid", borderColor: "grey.200", bgcolor: plt.d ? plt.bg : "inherit", borderTop: `3px solid ${plt.color}` }}>
                  <Typography variant="caption" sx={{ color: plt.color, fontWeight: 800, display: "block", mb: 1, textTransform: "uppercase" }}>{plt.id}</Typography>
                  {plt.d ? (
                    <>
                      <Typography variant="body2" fontWeight={800} mb={1} noWrap color="grey.900" title={plt.d[plt.keyName]}>{plt.d[plt.keyName]}</Typography>
                      {plt.rows.map((row, i) => <TelemetryRow key={i} label={row.l} value={row.v} />)}
                    </>
                  ) : (
                    <Box sx={{ mt: 2, p: 1, borderRadius: 1, textAlign: "center", border: "1px dashed", borderColor: "grey.300" }}>
                      <Typography variant="caption" color="text.disabled">Sin conexión</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* SECCIÓN 2.5: CHIPS ASOCIADOS */}
            {modem.numero_chip && modem.numero_chip.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>Chips Asociados</Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {modem.numero_chip.map((chip, i) => (
                    <Paper key={i} variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, display: "flex", alignItems: "center", gap: 2, bgcolor: "info.50", borderColor: "info.200" }}>
                      <SettingsInputAntennaIcon sx={{ color: "info.main" }} />
                      <Box>
                        <Typography variant="body2" fontWeight={800} color="info.900">{chip.numero}</Typography>
                        <Typography variant="caption" color="info.700">{chip.emai} • {chip.ciudad}</Typography>
                      </Box>
                      <StatusChip status={chip.estado} />
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {/* 3. SECCIÓN INFERIOR: HISTORIAL */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={800} color="grey.800">Historial de Movimientos</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" startIcon={<FileDownloadIcon />} sx={{ color: "primary.main", borderColor: "primary.300", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "primary.50" } }}>Exportar</Button>
                <Button variant="outlined" startIcon={<FilterListIcon />} sx={{ color: "primary.main", borderColor: "primary.300", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "primary.50" } }}>Filtrar</Button>
              </Box>
            </Box>

            <Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 2, overflow: "hidden" }}>
              <TableContainer sx={{ 
                maxHeight: 350, 
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#c1c1c1", borderRadius: 4 },
              }}>
                <Table stickyHeader sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {/* Cabecera tintada con azul primario muy suave */}
                      {["Fecha", "Operación / Req", "Placa", "Propiedad / Asignado", "Ciudad", "Estado", "Usuario"].map((h) => (
                        <TableCell key={h} sx={{ bgcolor: "#f0f4f8", color: "primary.900", fontWeight: 700, borderBottom: "2px solid", borderColor: "primary.100", py: 1.5, fontSize: "0.75rem", textTransform: "uppercase" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historial.map((h) => (
                      <TableRow key={h.id_historial} hover sx={{ "& td": { borderBottom: "1px solid", borderColor: "grey.100", py: 1.5 } }}>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{fmtFechaHoraSql(h.fecha)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} color="grey.900">{h.operacion}</Typography>
                          <Typography variant="caption" color="primary.main" fontWeight={600}>{h.cod_req || h.cod_ot || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} color="grey.900">{h.placa || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="grey.800">{h.asignado_descripcion || "—"}</Typography>
                          <Typography variant="caption" color="text.secondary">{h.propiedad_descripcion}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{h.ciudad}</TableCell>
                        <TableCell><StatusChip status={h.estado} /></TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: 500 }}>{h.usu}</TableCell>
                      </TableRow>
                    ))}
                    {historial.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">No se encontraron movimientos en el historial</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

          </Box>
        )}
      </DialogContent>
      
      <Snackbar 
        open={copySuccess} 
        autoHideDuration={2000} 
        onClose={() => setCopySuccess(false)} 
        message="ID copiado al portapapeles" 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
}